import { makeStyles } from '@material-ui/core'
import { GridColumns, DataGrid } from '@material-ui/data-grid'
import { ChevronRight } from '@material-ui/icons'
import { useRouter } from 'next/router'
import React from 'react'
import { FormattedNumber } from 'react-intl'

import { IPayoutReport } from '@aletheia/eafunds/contentful/types/generated'

import { isEntry } from '../../../lib/contentful/types'
import ButtonLink from '../../ButtonLink'
import FuzzySearch from '../../FuzzySearch'
import PrettyDate from '../utilities/PrettyDate'

const columns: GridColumns = [
  {
    field: 'date',
    headerName: 'Date',
    width: 120,
    disableColumnMenu: true,
    renderCell({ value }) {
      return <PrettyDate value={value as string} month="short" />
    },
  },
  {
    field: 'amount',
    headerName: 'Amount',
    width: 130,
    type: 'number',
    disableColumnMenu: true,
    renderCell({ value }) {
      return (
        <span
          style={{
            textAlign: 'right',
            display: 'inline-block',
            width: '100%',
          }}
        >
          <FormattedNumber
            currency="USD"
            style="currency"
            minimumFractionDigits={0}
            maximumFractionDigits={0}
            value={value as number}
          />
        </span>
      )
    },
  },
  {
    field: 'recipients',
    flex: 1,
    minWidth: 200,
    headerName: 'Grantee(s)',
    disableColumnMenu: true,
    valueGetter: ({ value }) => {
      return (value as IPayoutReport['fields']['recipients'])
        .filter(isEntry)
        .map((recipient) => recipient.fields.title)
        .join(', ')
    },
  },
  {
    width: 130,
    field: 'href',
    headerName: 'Full report',
    sortable: false,
    disableColumnMenu: true,
    renderCell({ value }) {
      return (
        <ButtonLink href={value as string}>
          Read More <ChevronRight />
        </ButtonLink>
      )
    },
  },
]

const useStyles = makeStyles((theme) => ({
  dataGridWrapper: {
    height: 390,
  },
  dataGrid: {
    border: 'none',
    '& .MuiDataGrid-row': {
      cursor: 'pointer',
    },
    '& .MuiDataGrid-colCell': {
      fontFamily: theme.typography.h1.fontFamily,
    },
  },
  searchResultWrapper: {
    display: 'flex',
  },
  searchResultText: {
    whiteSpace: 'nowrap',
  },
  searchResultRecipients: {
    flexShrink: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
  },
}))

export const FundPayoutReportList: React.FC<{
  PayoutReports: IPayoutReport[]
  pageSize?: number
}> = ({ PayoutReports, pageSize = 5 }) => {
  const classes = useStyles()
  const { push } = useRouter()
  if (!PayoutReports) return null

  return (
    <>
      <FuzzySearch
        keyBy="slug"
        list={PayoutReports.map((R) => R.fields)}
        options={{
          keys: ['title', 'recipients.fields.title'],
          includeMatches: true,
          threshold: 0.3,
          ignoreLocation: true,
        }}
        formatResults={({ date, recipients }, result) => {
          const matches = result?.matches?.filter(
            (match) => match.key === 'recipients.fields.title',
          )
          const remainingRecipients = recipients.length - (matches?.length || 0)
          return (
            <span className={classes.searchResultWrapper}>
              <span className={classes.searchResultText}>
                <PrettyDate value={date} day={undefined} />:{' '}
              </span>
              <span className={classes.searchResultRecipients}>
                {matches?.map((match) => match.value).join(', ')}
              </span>{' '}
              {remainingRecipients > 0 && (
                <span className={classes.searchResultText}>
                  (+ {remainingRecipients} more)
                </span>
              )}
            </span>
          )
        }}
        onSelectItem={({ slug }) => push(`/funds/payouts/${slug}`)}
      />
      <div className={classes.dataGridWrapper}>
        <DataGrid
          pageSize={pageSize}
          autoHeight
          rows={PayoutReports.map((R) => ({
            id: R.sys.id,
            href: `/payouts/${R.fields.slug}`,
            ...R.fields,
          }))}
          columns={columns}
          onRowClick={({ row }) => push(row.href)}
          sortingOrder={['desc', 'asc', null]}
          className={classes.dataGrid}
        />
      </div>
    </>
  )
}

export default FundPayoutReportList
